import { Link, useLocation } from 'react-router-dom';
import { moduleCards } from './DashboardLayout';
import { useEffect, useState, ReactNode } from 'react';
import api from '@/lib/api';

function Icon({ path, className = 'w-5 h-5' }: { path: string; className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
    </svg>
  );
}

const labelMap: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/products': 'Produtos',
  '/stock': 'Estoque',
  '/warehouses': 'Depósitos',
  '/carriers': 'Transportadoras',
  '/orders': 'Pedidos',
  '/picking': 'Separação',
  '/packing': 'Embalagem',
  '/shipping': 'Expedição',
  '/freight': 'Fretes',
  '/tracking': 'Rastreamento',
  '/returns': 'Devoluções',
  '/occurrences': 'Ocorrências',
  '/integrations': 'Integrações',
  '/reports': 'Relatórios',
  '/users': 'Usuários',
  '/audit': 'Auditoria',
  '/settings': 'Configurações',
};

export function ModuleTabs() {
  const { pathname } = useLocation();

  const findModule = () =>
    moduleCards.find((card) =>
      card.items.some((item) => pathname === item.href || pathname.startsWith(item.href + '/'))
    );

  const activeModule = findModule();

  if (!activeModule) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-dark-100 mb-6">
      <div className="px-6 pt-4 pb-2 border-b border-dark-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon path={activeModule.icon} className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-dark-900">{activeModule.title}</h2>
          </div>
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-dark-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <Icon path="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" className="w-4 h-4" />
            <span>Início</span>
          </Link>
        </div>
      </div>
      <div className="px-6 pb-2 overflow-x-auto">
        <div className="flex gap-1">
          {activeModule.items.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-dark-500 hover:text-dark-900 hover:border-dark-300'
                }`}
              >
                <Icon path={item.icon} className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type Column<T> = {
  key: keyof T | string;
  label: string;
  render?: (item: T) => ReactNode;
};

type ModuleTableConfig<T> = {
  endpoint: string;
  columns: Column<T>[];
  searchKey?: keyof T;
};

export function ModuleTable<T extends { id: string }>({ config }: { config: ModuleTableConfig<T> }) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(config.endpoint)
      .then((r) => {
        const data = r.data?.data || r.data || [];
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [config.endpoint]);

  const filtered = items.filter((item) => {
    if (!search || !config.searchKey) return true;
    const val = (item as any)[config.searchKey];
    return val && String(val).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar..."
          className="px-4 py-2 border border-dark-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-50">
              <tr>
                {config.columns.map((col) => (
                  <th
                    key={String(col.key)}
                    className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {filtered.map((item, index) => (
                <tr
                  key={item.id}
                  className={`hover:bg-primary-50 cursor-pointer transition-colors ${
                    index % 2 === 0 ? 'bg-white' : 'bg-dark-50/50'
                  }`}
                >
                  {config.columns.map((col) => (
                    <td key={String(col.key)} className="px-6 py-4 text-sm text-dark-900">
                      {col.render
                        ? col.render(item)
                        : String((item as any)[col.key] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-dark-500">Nenhum registro encontrado</div>
          )}
        </div>
      )}
    </div>
  );
}

export function getPageTitle(pathname: string): string {
  if (pathname.startsWith('/orders/')) return 'Detalhe do Pedido';
  return labelMap[pathname] || 'Stock-In';
}
